import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { motion } from 'framer-motion';

const REVIEWS = [
  {
    name: 'Yohanna Hinojosa Hiciano',
    initials: 'YH',
    avatarColor: '#1a73e8',
    rating: 5,
    date: 'hace 2 meses',
    text: 'La calidad del pescado y el producto es muy bueno, y además a buenos precios.',
  },
  {
    name: 'Patricia Torres',
    initials: 'PT',
    avatarColor: '#34a853',
    rating: 5,
    date: 'hace 3 meses',
    text: 'Soy clienta desde hace 6 años, y nunca me ha decepcionado. Toda la comida está buenísima, y los productos siempre son frescos y de calidad. Además, son atentos y cuidadosos con todo tipo de alergias e intolerancias. ',
  },
  {
    name: 'Belén',
    initials: 'LM',
    avatarColor: '#ea4335',
    rating: 5,
    date: 'hace 1 mes',
    text: 'Sin duda el mejor sushi que probado. Soy clienta habitual y es que me encanta. Muy buen producto.',
  },
];

const GoogleG: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const Stars: React.FC<{ count: number; size?: number }> = ({ count, size = 16 }) => (
  <Box sx={{ display: 'flex', gap: 0.25 }}>
    {Array.from({ length: count }).map((_, i) => (
      <StarIcon key={i} sx={{ fontSize: size, color: '#f9ab00' }} />
    ))}
  </Box>
);

type Review = typeof REVIEWS[0];

const ReviewCard: React.FC<Review & { index: number }> = ({
  name,
  initials,
  avatarColor,
  rating,
  date,
  text,
  index,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.55, delay: index * 0.13 }}
    style={{ height: '100%', display: 'flex' }}
  >
    <Box
      sx={{
        flex: 1,
        p: 3.5,
        borderRadius: 2,
        border: '1px solid var(--color-border)',
        bgcolor: 'var(--color-bg-card)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 0.3s',
        '&:hover': { borderColor: 'var(--color-border-hover)' },
      }}
    >
      {/* Reviewer row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              bgcolor: avatarColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.82rem',
              fontWeight: 700,
              color: '#fff',
              flexShrink: 0,
              letterSpacing: '0.03em',
            }}
          >
            {initials}
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--color-text-primary)', lineHeight: 1.2 }}>
              {name}
            </Typography>
            <Typography sx={{ fontSize: '0.73rem', color: 'var(--color-text-secondary)', mt: 0.25 }}>
              {date}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ opacity: 0.65, flexShrink: 0 }}>
          <GoogleG />
        </Box>
      </Box>

      <Box sx={{ mb: 1.75 }}>
        <Stars count={rating} />
      </Box>

      <Typography
        variant="body2"
        sx={{
          color: 'var(--color-text-secondary)',
          lineHeight: 1.9,
          fontStyle: 'italic',
          flex: 1,
        }}
      >
        "{text}"
      </Typography>
    </Box>
  </motion.div>
);

const GOOGLE_MAPS_URL =
  'https://www.google.com/maps/place/Terra+sushi/@40.4819071,-3.3981979,17z/data=!4m8!3m7!1s0xd4249be38f482e9:0x3cb7fba694178023!8m2!3d40.481903!4d-3.395623!9m1!1b1!16s%2Fg%2F11bwfkjchx';

const Testimonials: React.FC = () => {
  return (
    <Box id="testimonials" sx={{ py: 10, background: 'var(--color-bg-section)' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="overline"
            align="center"
            sx={{ display: 'block', color: 'var(--color-accent)', letterSpacing: '0.3em', mb: 1 }}
          >
            Opiniones
          </Typography>
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontFamily: 'var(--font-heading)',
              fontSize: { xs: '1.8rem', md: '2.5rem' },
              mb: 1.5,
            }}
          >
            Lo que dicen nuestros clientes
          </Typography>

          {/* Aggregate Google rating */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, mb: 8 }}
          >
            <GoogleG />
            <Typography sx={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
              4.6
            </Typography>
            <Stars count={5} size={14} />
            <Typography sx={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
              · Google Reviews
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={3}>
          {REVIEWS.map((r, i) => (
            <Grid key={i} size={{ xs: 12, md: 4 }}>
              <ReviewCard {...r} index={i} />
            </Grid>
          ))}
        </Grid>

        {/* View on Google CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.45 }}
        >
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography
              component="a"
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.25,
                color: 'var(--color-text-secondary)',
                fontSize: '0.85rem',
                textDecoration: 'none',
                border: '1px solid var(--color-border)',
                px: 3,
                py: 1.25,
                borderRadius: 2,
                transition: 'border-color 0.25s, color 0.25s',
                '&:hover': {
                  borderColor: 'var(--color-border-hover)',
                  color: 'var(--color-text-primary)',
                },
              }}
            >
              <GoogleG />
              Ver todas las opiniones en Google
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Testimonials;
